import { Injectable, inject } from '@angular/core';
import { UserModel } from '@portal/app/shared/models/userModel';
import { environment } from '@portal/environments/environment';
import { SelectionService } from '@portal/app/shared/services/selection.service';
import * as Sentry from '@sentry/angular';
import type { EventHint } from '@sentry/angular';

@Injectable({
  providedIn: 'root'
})
export class SentryService {
  private selectionService = inject(SelectionService);

  public setupSentry(user: UserModel): void {
    if (!environment.production) {
      return;
    }

    const email = user.getEmail();
    if (!email) {
      console.warn('Sentry user setup aborted: Missing email address');
      return;
    }

    Sentry.setUser({
      email: email,
      username: user.getUsername()
    });

    Sentry.setTag('userType', user.getUserType());

    const { clientId, brandId, brandName } =
      this.selectionService.clientBrandSignal();
    Sentry.setTag('clientId', String(clientId));
    Sentry.setTag('brandId', String(brandId));
    Sentry.setTag('brandName', brandName);
  }

  public clearUser(): void {
    if (!environment.production) {
      return;
    }
    Sentry.setUser(null);
  }

  /**
   * Count - track events (clicks, API calls, orders, etc.)
   * @param name - Metric name (e.g., 'button_click', 'api_call')
   * @param value - Count value (default: 1)
   * @param attributes - Optional key-value pairs for filtering/grouping
   */
  public count(
    name: string,
    value = 1,
    attributes?: Record<string, string>
  ): void {
    if (!environment.production) {
      return;
    }
    Sentry.metrics.count(name, value, { attributes });
  }

  /**
   * Gauge - track current values (queue depth, active connections, etc.)
   * @param name - Metric name (e.g., 'active_users', 'queue_depth')
   * @param value - Current value
   * @param attributes - Optional key-value pairs for filtering/grouping
   */
  public gauge(
    name: string,
    value: number,
    attributes?: Record<string, string>
  ): void {
    if (!environment.production) {
      return;
    }
    Sentry.metrics.gauge(name, value, { attributes });
  }

  /**
   * Distribution - track value ranges (response times, payload sizes, etc.)
   * @param name - Metric name (e.g., 'api_response_time', 'file_size')
   * @param value - Measured value
   * @param unit - Optional unit (e.g., 'millisecond', 'byte')
   * @param attributes - Optional key-value pairs for filtering/grouping
   */
  public distribution(
    name: string,
    value: number,
    unit?: string,
    attributes?: Record<string, string>
  ): void {
    if (!environment.production) {
      return;
    }
    Sentry.metrics.distribution(name, value, { unit, attributes });
  }

  /**
   * Submits feedback to Sentry using the captureFeedback API.
   * This method is used by the custom feedback modal component.
   *
   * @param message - The feedback message from the user
   * @param category - The request category with label, value, and Jira option ID
   * @param screenshot - Optional base64-encoded screenshot
   * @param userInfo - Optional user info (email and name) for the feedback
   * @returns Promise<boolean> - true if successful, false otherwise
   */
  public async submitFeedback(
    message: string,
    category: { value: string; jiraOptionId: number; label: string },
    screenshot?: string,
    userInfo?: { email?: string; name?: string }
  ): Promise<boolean> {
    if (!environment.production) {
      return false;
    }
    try {
      const { clientName, clientId, brandName, brandId } =
        this.selectionService.clientBrandSignal();

      // Enrich message with category metadata for Jira Automation parsing
      const enrichedMessage = `${message}\n\n---\nRequest Category: ${category.label}\nrequestCategoryJiraId: ${category.jiraOptionId}\nclientName: ${clientName}\nclientId: ${clientId}\nbrandName: ${brandName}\nbrandId: ${brandId}`;

      const hints: EventHint = {
        captureContext: {
          tags: {
            // Tags for Sentry-Jira integration mapping
            requestCategory: category.value,
            requestCategoryJiraId: String(category.jiraOptionId)
          }
        }
      };

      // Add screenshot attachment to the current scope
      if (screenshot) {
        // Convert base64 data URL to Uint8Array
        const base64Data = screenshot.replace(/^data:image\/png;base64,/, '');
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        Sentry.getCurrentScope().addAttachment({
          filename: 'screenshot.png',
          data: bytes,
          contentType: 'image/png'
        });
      }

      Sentry.captureFeedback(
        {
          message: enrichedMessage,
          email: userInfo?.email,
          name: userInfo?.name
        },
        hints
      );

      // Clear attachments after sending to avoid duplicates
      if (screenshot) {
        Sentry.getCurrentScope().clearAttachments();
      }

      // Flush the replay buffer so a session replay is always attached to feedback
      const replay = Sentry.getReplay();
      if (replay) {
        await replay.flush();
      }

      return true;
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      return false;
    }
  }
}
